// @ts-strict-ignore
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Descendant } from 'slate';
import { useDispatch, useSelector } from 'src/store/store';
import i18n from 'src/utils/translate';
import { editPostReply, respondPost } from 'src/modules/community/singlePost/actions/actions';

import Avatar, { Size as AvatarSize } from 'src/components/avatar';
import SpinnerOverlay, { Size } from 'src/components/spinnerOverlay';
import Button from 'src/components/buttons/button';
import FollowButton from 'src/components/buttons/followButton/followButton';
import ReplyEditor from 'src/modules/community/singlePost/components/replies/replyEditor';
import { useIsCommunityMember } from 'src/utils/context';
import { composeHtmlStringFromBB, useDeserialiseHtmlString } from 'src/components/textEditor/deserialiseContent';
import { HIDDEN_USERNAME } from 'src/constants/hiddenUser';
import ReplyEditorAndroid from 'src/modules/community/singlePost/components/replies/replyEditorAndroid';
import Icon from 'src/components/icon';

type AddReplyProps = {
  inReplyToUsername?: string,
  replyData?: { // no reply data = new reply to a superparent
    responseId?: number, // = the id of edited response, OR the response we want to respond to.
    parentId?: number,
    body?: string,
    imageHash?: string,
    imageUrl?: string,
    imageDescription?: string,
  };
  respondRef?: MutableRefObject<null>,
  onCancel?: () => void;
}

export default function WriteReplyBox({ inReplyToUsername, onCancel, replyData = {}, respondRef }: AddReplyProps): JSX.Element {
  const dispatch = useDispatch();

  const isEditReply = !!replyData?.body;

  const [isPosting, setIsPosting] = useState(false); // = is the post being sent

  const { currentUser, currentCommunity, isAndroidUser } = useSelector((state) => state.context);
  const { post, composeReply } = useSelector((state) => state.pages.singlePost);
  const { author, isPrivate, mentionedUsers } = post;
  const superPostAuthorUsername = (!author.isDeleted && !author.isHidden)
    ? author.username
    : i18n.t(HIDDEN_USERNAME);

  const isCommunityMember = useIsCommunityMember();
  const isCurrentCommunityMemberOrVisitor = !currentUser || isCommunityMember;
  const isPostPrivate = isPrivate && !isCommunityMember;

  const postOrEdit = (editorData) => {
    setIsPosting(true);
    const postData = {
      ...editorData,
      postId: replyData.responseId || post.id,
      superParentId: post.id,
      ...(isEditReply && { parentId: replyData.parentId ?? post.id }),
      communitySlug: currentCommunity.slug,
    };
    dispatch(isEditReply ? editPostReply(postData) : respondPost(postData));
  };

  const initialRender = useRef(true);
  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    setIsPosting(false);
  }, [composeReply.submittedReplyId]);

  const deserialisedHtmlString = useDeserialiseHtmlString({
    text: composeHtmlStringFromBB({ text: replyData.body || '' }),
    image: { hash: replyData?.imageHash, url: replyData?.imageUrl, description: replyData?.imageDescription ?? '' },
    mentionedUsers,
  });

  const replyEditorValue = (!isAndroidUser && isEditReply) ? deserialisedHtmlString : [{ type: 'paragraph', children: [{ text: '' }] }];

  if (isPostPrivate) return null;

  return (
    <RespondContainer>
      {isPosting && <SpinnerOverlay margin={'8% auto'} size={Size.M} wholePage={false} />}
      <div>
        {!isEditReply && (
          <RespondHeader>
            {currentUser && <Avatar alt="Your avatar" size={AvatarSize.Small} url={currentUser.avatarImageUrl} />}
            <RespondHeaderContentWrapper>
              <RespondHeaderContent>
                <div>
                  {i18n.t('Reply to')}{' '}{inReplyToUsername ?? superPostAuthorUsername}
                </div>
                {onCancel && (
                  <Button type="none" onClick={onCancel}>
                    <Icon icon="cross" title="Close" />
                  </Button>
                )}
              </RespondHeaderContent>
              {!isCurrentCommunityMemberOrVisitor && (
                <FollowButton
                  buttonType="none"
                  community={currentCommunity}
                  followType="community"
                />
              )}
            </RespondHeaderContentWrapper>
          </RespondHeader>
        )}
        {currentUser && isCommunityMember && (
          <>
            {isAndroidUser ? (
              <ReplyEditorAndroid
                autoFocus={replyData.responseId ? true : false}
                buttonLabel={isEditReply ? i18n.t('Edit response') : i18n.t('Reply')}
                editorValue={replyData.body}
                image={{ hash: replyData?.imageHash, url: replyData?.imageUrl, description: replyData?.imageDescription ?? '', children: null }}
                postOrEdit={postOrEdit}
                respondRef={respondRef}
              />
            ) : (
              <ReplyEditor
                autoFocus={replyData.responseId ? true : false}
                buttonLabel={isEditReply ? i18n.t('Edit response') : i18n.t('Reply')}
                postOrEdit={postOrEdit}
                replyEditorValue={replyEditorValue as Descendant[]}
                respondRef={respondRef}
              />
            )}
          </>
        )}
      </div>
    </RespondContainer>
  );
}

const RespondContainer = styled.div`
  margin: 10px 0 20px;
  position: relative;
`;

const RespondHeader = styled.div`
  display: flex;
  align-items: center;
`;

const RespondHeaderContentWrapper = styled.div`
  width: 100%;
  margin-left: 10px;
  button {
    font-size: ${props => props.theme.fontSizeM};
    @media (${props => props.theme.underScreenSmall}) {
      padding-top: 0;
    }
  }
`;

const RespondHeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
`;
